// "Rewind to here" (#3629 C), moved out of state.svelte.ts: the rewind, then the page after
// it replaces the view as another life.
import { reportUiFailure } from "../../../report-failure.js";
import { workspaceState } from "../../../core/state.svelte.js";
import { olderProof } from "../mirror-reset.js";
import {
  agentStreamState,
  reconcileAgentTranscript,
  staleAfter,
  type TimelineFetchClient,
} from "../state.svelte.js";

interface RewindClient extends TimelineFetchClient {
  rewindAgent(workspaceId: string, agentId: string, messageId: string): Promise<void>;
}

// "Rewind to here": every read begun before is stale from the first line (#3382, M2), the
// page after it replaces the view as another life, and a failed re-read is reported.
export async function rewindConversation(
  client: RewindClient,
  agentId: string,
  messageId: string,
): Promise<void> {
  const wsId = workspaceState.current?.id;
  if (!wsId) throw new Error("No workspace selected");
  agentStreamState.invalidateReads(agentId);
  const cut = staleAfter(agentId, wsId);
  try {
    await client.rewindAgent(wsId, agentId, messageId);
  } catch (err) {
    // Handled by the caller (the rewind failed). The reads it cut restart, unless a newer
    // rewind, clear or workspace switch owns the conversation now.
    if (!cut()) void reconcileAgentTranscript(client, agentId);
    throw err;
  }
  // A workspace switch or a newer rewind/clear meanwhile: this re-read is not theirs.
  if (cut()) return;
  // The rewind landed: reads begun while it was out read the old life, so they go stale too.
  agentStreamState.invalidateReads(agentId);
  const stale = staleAfter(agentId, wsId);
  try {
    const daemonId = agentStreamState.routeOf(agentId);
    const page = await client.fetchAgentTimeline(
      wsId,
      agentId,
      daemonId ? { daemonId } : undefined,
    );
    if (stale()) return;
    // mode "rewind": the one legitimate shrink, past the monotonic guard. The rewind landed, so
    // its page retires the old life: empty after a rewind to the first turn is the real view.
    agentStreamState.hydrateFromTimeline(agentId, page.items, {
      retired: true,
      hasOlder: page.hasOlder,
      olderProven: olderProof(page),
      olderCursor: page.olderCursor,
      maxSeq: page.maxSeq,
      life: page.life,
      mode: "rewind",
    });
  } catch (err) {
    // The rewind landed: the recovery reads the rewound view back (it refuses one that moved on).
    reportUiFailure("agents.rewind.refetch", err, { agentId, workspaceId: wsId });
    void reconcileAgentTranscript(client, agentId);
  }
}
